// Orders.deliveryTerms is stored as a full free-text sentence, e.g.
// "Estimated delivery is 3 to 5 working days" (see the deliveryterms
// table / GET /attributes/dterms for where that description comes from).
// Dispatch date always takes the upper bound of the day range in it, since
// that's the latest day the order can still go out on time. Added to
// paymentDate (when the order actually cleared payment), not orderDate -
// an order can sit unpaid for a while before that starts counting.

export function maxDeliveryDays(deliveryTerms: string | null | undefined): number | null {
  if (!deliveryTerms) return null;
  const matches = deliveryTerms.match(/\d+/g);
  if (!matches || !matches.length) return null;
  return Math.max(...matches.map(Number));
}

// Condenses the free-text sentence down to just the day range, e.g.
// "Estimated delivery is 3 to 5 working days" -> "3-5". Falls back to the
// original text if no numbers are found (an unexpected/custom terms
// string), so it's never silently blank.
export function formatDeliveryTerms(deliveryTerms: string | null | undefined): string | null {
  if (!deliveryTerms) return null;
  // A handful of existing orders have a broken "Estimated delivery is NaN
  // to NaN working days" terms string (orders placed before the NaN/
  // malformed-delivery-term guard was added). Collapse that down to a bare
  // "NaN" instead of falling through to the raw sentence - the fallback
  // below is for genuinely non-numeric terms like "please contact
  // customer support", which should still show in full.
  if (/nan/i.test(deliveryTerms)) return 'NaN';
  const numbers = deliveryTerms.match(/\d+/g);
  if (!numbers?.length) return deliveryTerms;
  const range = Array.from(new Set([numbers[0], numbers[numbers.length - 1]]));
  return range.join('-');
}

export function computeDispatchDate(order: {
  deliveryTerms?: string | null;
  paymentDate?: string | Date | null;
}): Date | null {
  const days = maxDeliveryDays(order.deliveryTerms);
  if (days === null || !order.paymentDate) return null;
  const base = new Date(order.paymentDate);
  if (isNaN(base.getTime())) return null;
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  return result;
}

// green = upcoming, amber = due today, red = overdue, gray = unknown
// (missing payment date or unparseable delivery terms).
export function dispatchUrgencyClass(date: Date | null): string {
  if (!date) return 'bg-gray-100 text-gray-500';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  if (target.getTime() < today.getTime()) return 'bg-red-100 text-red-700';
  if (target.getTime() === today.getTime()) return 'bg-amber-100 text-amber-700';
  return 'bg-green-50 text-green-700';
}
